import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import type { SxProps, Theme } from '@mui/material/styles'
import type { LottieRefCurrentProps } from 'lottie-react'

const LazyLottie = lazy(() => import('lottie-react'))

export type CelebrationAnimationProps = Readonly<{
  /** Lottie JSON — preferred when a `.json` export is available. */
  animationData?: object
  /** Animated GIF fallback until Lottie JSON is supplied. */
  gifSrc?: string
  /** Static image when `prefers-reduced-motion: reduce` is set. */
  staticFallbackSrc?: string
  width?: number | string
  height?: number | string
  loop?: boolean
  playing?: boolean
  sx?: SxProps<Theme>
  'data-testid'?: string
}>

export function CelebrationAnimation({
  animationData,
  gifSrc,
  staticFallbackSrc,
  width = '100%',
  height = 'auto',
  loop = false,
  playing = true,
  sx,
  'data-testid': testId = 'celebration-animation',
}: CelebrationAnimationProps) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const lottieRef = useRef<LottieRefCurrentProps | null>(null)
  const [gifKey, setGifKey] = useState(0)

  useEffect(() => {
    if (!playing || reducedMotion) return
    if (animationData) {
      lottieRef.current?.goToAndPlay(0)
      return
    }
    if (gifSrc) {
      setGifKey((key) => key + 1)
    }
  }, [playing, reducedMotion, animationData, gifSrc])

  const commonSx: SxProps<Theme> = {
    width,
    height,
    maxWidth: '100%',
    mx: 'auto',
    display: 'block',
    ...sx,
  }

  if (reducedMotion && staticFallbackSrc) {
    return (
      <Box
        component="img"
        src={staticFallbackSrc}
        alt=""
        aria-hidden
        data-testid={testId}
        sx={commonSx}
      />
    )
  }

  if (animationData && !reducedMotion && playing) {
    return (
      <Box data-testid={testId} sx={{ ...commonSx, lineHeight: 0 }}>
        <Suspense fallback={null}>
          <LazyLottie
            lottieRef={lottieRef}
            animationData={animationData}
            loop={loop}
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </Suspense>
      </Box>
    )
  }

  const motionSrc = gifSrc ?? staticFallbackSrc
  if (motionSrc) {
    return (
      <Box
        key={gifKey}
        component="img"
        src={motionSrc}
        alt=""
        aria-hidden
        data-testid={testId}
        sx={commonSx}
      />
    )
  }

  return null
}
